import { useState, useEffect } from 'react';
import {
  Button,
  Spinner,
  Text,
  Title3,
  Badge,
  Textarea,
  Card,
  CardHeader,
  Divider,
  Tooltip,
  Switch,
  Input,
  Select,
  Tab,
  TabList,
} from '@fluentui/react-components';
import TimeEntryFields from './TimeEntryFields';
import {
  Mail24Regular,
  Person24Regular,
  Building24Regular,
  CheckmarkCircle24Filled,
  ArrowSync24Regular,
  Settings24Regular,
  Add24Regular,
} from '@fluentui/react-icons';
import ThemeToggle from './ThemeToggle';
import { useTheme } from '../hooks/useTheme';
import { getEmailContext, EmailContext } from '../services/office-context';
import {
  matchEmails,
  createEmailActivity,
  syncContacts,
  getCachedContacts,
  getLastSyncDate,
  clearConfig,
  CWContact,
  CWTicket,
  CWOpportunity,
  fetchTicketsForContact,
  fetchOpportunitiesForContact,
  createFollowUpActivity,
  createTicket,
  createOpportunity,
  createTimeEntry,
  fetchServiceBoards,
  fetchWorkTypes,
  searchCompanies,
  fetchTicketsForCompany,
  fetchOpportunitiesForCompany,
  fetchBoardStatuses,
  updateTicketStatus,
  syncMemberEmails,
  CWWorkType,
  CWCompany,
  CWStatus,
  getLogTimeOnOpps,
  setLogTimeOnOpps,
  getDefaultAddTimeEntry,
  setDefaultAddTimeEntry,
} from '../services/cw-api';

interface Props {
  onReset: () => void;
}

type LinkType = 'none' | 'ticket' | 'opportunity';

export default function TaskPane({ onReset }: Props) {
  const [emailCtx, setEmailCtx] = useState<EmailContext | null>(null);
  const [matches, setMatches] = useState<CWContact[]>([]);
  const [selectedContact, setSelectedContact] = useState<CWContact | null>(null);
  const [notes, setNotes] = useState('');
  const [logging, setLogging] = useState(false);
  const [logged, setLogged] = useState(false);
  const [loggedId, setLoggedId] = useState<number | null>(null);
  const [error, setError] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [syncProgress, setSyncProgress] = useState('');
  const [contactCount, setContactCount] = useState(getCachedContacts().length);
  const [showSettings, setShowSettings] = useState(false);
  const [logTimeOnOppsState, setLogTimeOnOppsState] = useState(() => getLogTimeOnOpps());
  const [defaultAddTimeEntryState, setDefaultAddTimeEntryState] = useState(() => getDefaultAddTimeEntry());

  // Company fallback (when no contact matched)
  const [companySearch, setCompanySearch] = useState('');
  const [companyResults, setCompanyResults] = useState<CWCompany[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<CWCompany | null>(null);
  const [searchingCompanies, setSearchingCompanies] = useState(false);

  // Ticket/Opp linking
  const [tickets, setTickets] = useState<CWTicket[]>([]);
  const [opportunities, setOpportunities] = useState<CWOpportunity[]>([]);
  const [loadingLinks, setLoadingLinks] = useState(false);
  const [includeClosed, setIncludeClosed] = useState(false);
  const [linkType, setLinkType] = useState<LinkType>('none');
  const [selectedTicket, setSelectedTicket] = useState<CWTicket | null>(null);
  const [selectedOpp, setSelectedOpp] = useState<CWOpportunity | null>(null);
  const [boardStatuses, setBoardStatuses] = useState<CWStatus[]>([]);
  const [newTicketStatus, setNewTicketStatus] = useState<number | null>(null);

  // Follow-up
  const [createFollowUp, setCreateFollowUp] = useState(false);
  const [followUpNotes, setFollowUpNotes] = useState('');
  const [followUpDate, setFollowUpDate] = useState('');
  const [followUpCreated, setFollowUpCreated] = useState(false);

  // Time entry
  const [addTimeEntry, setAddTimeEntry] = useState(() => getDefaultAddTimeEntry());
  const [timeHours, setTimeHours] = useState('');
  const [timeNotes, setTimeNotes] = useState('');
  const [timeEntryCreated, setTimeEntryCreated] = useState(false);
  const [workTypes, setWorkTypes] = useState<CWWorkType[]>([]);
  const [selectedWorkType, setSelectedWorkType] = useState<number | undefined>();
  const [billableOption, setBillableOption] = useState<'Billable' | 'DoNotBill' | 'NoCharge'>('Billable');

  // New ticket creation
  const [showCreateTicket, setShowCreateTicket] = useState(false);
  const [newTicketSummary, setNewTicketSummary] = useState('');
  const [newTicketBoard, setNewTicketBoard] = useState<number | undefined>();
  const [boards, setBoards] = useState<{ id: number; name: string }[]>([]);
  const [creatingTicket, setCreatingTicket] = useState(false);

  // New opportunity creation
  const [showCreateOpp, setShowCreateOpp] = useState(false);
  const [newOppName, setNewOppName] = useState('');
  const [creatingOpp, setCreatingOpp] = useState(false);

  useEffect(() => {
    loadEmail();
  }, []);

  // In compose mode, the user typically opens the pane BEFORE entering a
  // recipient or body. Subscribe to Office.EventType.RecipientsChanged so
  // the contact search re-runs when the user types a recipient after the
  // pane is already open. Non-destructive: refreshes matches + emailCtx
  // without wiping the user's in-progress notes/selection.
  useEffect(() => {
    if (typeof Office === 'undefined') return;
    const item: any = Office.context?.mailbox?.item;
    if (!item?.addHandlerAsync) return; // not in compose mode
    const onChange = () => { void refreshFromOffice(); };
    try {
      item.addHandlerAsync(Office.EventType.RecipientsChanged, onChange);
    } catch {
      // older Outlook may throw — ignore, fall back to once-on-mount load
    }
    return () => {
      try {
        item.removeHandlerAsync(Office.EventType.RecipientsChanged, { handler: onChange });
      } catch { /* ignore */ }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedContact || selectedCompany) loadLinkedItems();
  }, [selectedContact, selectedCompany, includeClosed]);

  /**
   * Non-destructive refresh — fetch the latest email context from
   * Office.js, replace emailCtx, re-run matching against new recipients.
   * Does NOT reset selectedContact / notes / selections — if the user
   * has already picked something, their work stays. If the new recipient
   * introduces a better match and no contact is currently selected, it
   * auto-selects like the initial load does.
   */
  const refreshFromOffice = async () => {
    const ctx = await getEmailContext();
    setEmailCtx(ctx);
    const matched = matchEmails(ctx.allAddresses);
    setMatches(matched);
    if (!selectedContact && matched.length === 1) setSelectedContact(matched[0]);
  };

  const loadEmail = async () => {
    const ctx = await getEmailContext();
    setEmailCtx(ctx);
    setLogged(false);
    setLoggedId(null);
    setNotes('');
    setError('');
    setSelectedContact(null);
    setSelectedTicket(null);
    setSelectedOpp(null);
    setLinkType('none');
    setCreateFollowUp(false);
    setFollowUpCreated(false);
    setAddTimeEntry(false);
    setTimeEntryCreated(false);

    if (getCachedContacts().length === 0) await handleSync();
    syncMemberEmails(); // Cache member emails for exclusion (non-blocking)

    const matched = matchEmails(ctx.allAddresses);
    setMatches(matched);
    if (matched.length === 1) setSelectedContact(matched[0]);
  };

  const loadLinkedItems = async () => {
    // Load tickets/opps by COMPANY (shows all company items, not just contact-specific)
    const companyId = selectedContact?.company?.id || selectedCompany?.id;
    if (!companyId) return;
    setLoadingLinks(true);
    try {
      const [tix, opps] = await Promise.all([
        fetchTicketsForCompany(companyId, includeClosed),
        fetchOpportunitiesForCompany(companyId, includeClosed),
      ]);
      setTickets(tix);
      setOpportunities(opps);
    } catch (err) {
      console.error('Failed to load tickets/opps:', err);
    }
    setLoadingLinks(false);
  };

  const handleSync = async () => {
    setSyncing(true);
    setSyncProgress('Starting...');
    try {
      const contacts = await syncContacts((page, count) => {
        setSyncProgress(`Page ${page}... (${count} contacts)`);
      });
      setContactCount(contacts.length);
      setSyncProgress('');
      if (emailCtx) {
        const matched = matchEmails(emailCtx.allAddresses);
        setMatches(matched);
        if (matched.length === 1) setSelectedContact(matched[0]);
      }
    } catch (err) {
      const msg = String(err);
      if (msg.includes('Failed to fetch') || msg.includes('NetworkError')) {
        setError('Could not reach ConnectWise API. Check your connection.');
      } else {
        setError(`Sync failed: ${msg.replace(/^Error:\s*/, '')}`);
      }
    }
    setSyncing(false);
  };

  const handleCompanySearch = async (query: string) => {
    setCompanySearch(query);
    if (query.length < 2) { setCompanyResults([]); return; }
    setSearchingCompanies(true);
    try {
      const results = await searchCompanies(query);
      setCompanyResults(results);
    } catch (err) {
      console.error('Company search failed:', err);
    }
    setSearchingCompanies(false);
  };

  const handleLog = async () => {
    if ((!selectedContact && !selectedCompany) || !emailCtx) return;
    setLogging(true);
    setError('');

    const contactId = selectedContact?.id || 0;
    const companyId = selectedContact?.company?.id || selectedCompany?.id || 0;

    try {
      // In compose mode, the body + recipients + subject can change
      // after the pane was first opened. Re-read them right before we
      // POST so the activity captures what the user is actually about
      // to send (including body text typed after pane open).
      const freshCtx = emailCtx.isCompose ? await getEmailContext() : emailCtx;
      if (emailCtx.isCompose) setEmailCtx(freshCtx);

      const noteLines = [
        `Subject: ${freshCtx.subject}`,
        `From: ${freshCtx.from}`,
        `To: ${freshCtx.to.join(', ')}`,
        freshCtx.cc.length ? `CC: ${freshCtx.cc.join(', ')}` : '',
        '',
        notes,
        '',
        '--- Email Body ---',
        freshCtx.body,
      ].filter(Boolean).join('\n');

      const result = await createEmailActivity({
        subject: freshCtx.subject,
        contactId: contactId,
        companyId: companyId,
        email: selectedContact?.emails[0] || freshCtx.from,
        notes: noteLines,
        dateStart: freshCtx.dateCreated,
        ticketId: selectedTicket?.id,
        opportunityId: selectedOpp?.id,
      });

      setLogged(true);
      setLoggedId(result.id);

      // Update ticket status if changed
      if (selectedTicket && newTicketStatus && newTicketStatus !== selectedTicket.status?.id) {
        try {
          await updateTicketStatus(selectedTicket.id, newTicketStatus);
        } catch (err) {
          setError(`Email logged but ticket status update failed: ${String(err)}`);
        }
      }

      // Create follow-up if requested
      if (createFollowUp && followUpNotes && followUpDate) {
        try {
          await createFollowUpActivity({
            contactId: contactId,
            companyId: companyId,
            notes: followUpNotes,
            dateStart: new Date(followUpDate).toISOString().replace(/\.\d{3}Z$/, 'Z'),
            ticketId: selectedTicket?.id,
            opportunityId: selectedOpp?.id,
          });
          setFollowUpCreated(true);
        } catch (err) {
          setError(`Email logged but follow-up failed: ${String(err)}`);
        }
      }

      // Create time entry if requested — supports both Ticket and Opportunity
      // (opp time is pre-sales work; gated by logTimeOnOpps setting, default on)
      if (addTimeEntry && timeHours) {
        // For opportunity time, charge to the email Activity we just logged
        // (result.id) — CW books pre-sales time against the activity, not the
        // opportunity record directly.
        const chargeTo = selectedTicket
          ? { type: 'Ticket' as const, id: selectedTicket.id }
          : selectedOpp && getLogTimeOnOpps()
          ? { type: 'Opportunity' as const, id: result.id }
          : null;
        if (chargeTo) {
          try {
            await createTimeEntry({
              memberId: 0,
              chargeToType: chargeTo.type,
              chargeToId: chargeTo.id,
              timeStart: emailCtx.dateCreated.replace(/\.\d{3}Z$/, 'Z'),
              actualHours: parseFloat(timeHours),
              notes: timeNotes || `Email correspondence: ${emailCtx.subject}`,
              workTypeId: selectedWorkType,
              billableOption: billableOption,
            });
            setTimeEntryCreated(true);
          } catch (err) {
            setError(`Email logged but time entry failed: ${String(err)}`);
          }
        }
      }
    } catch (err) {
      const msg = String(err);
      if (msg.includes('Failed to fetch')) {
        setError('Could not reach ConnectWise API.');
      } else if (msg.includes('400')) {
        setError('CW rejected the request. Please try again.');
      } else {
        setError(`Failed: ${msg.replace(/^Error:\s*/, '')}`);
      }
    }
    setLogging(false);
  };

  const handleCreateTicket = async () => {
    if (!emailCtx) return;
    if (!selectedContact) {
      // Surface the reason instead of silently doing nothing — a ticket needs a
      // contact to attach to, and the create form can open with only a company picked.
      setError('Select a contact before creating a ticket — a ticket attaches to a contact.');
      return;
    }
    setCreatingTicket(true);
    try {
      if (boards.length === 0) {
        const b = await fetchServiceBoards();
        setBoards(b);
        if (b.length > 0 && !newTicketBoard) setNewTicketBoard(b[0].id);
      }
      const summary = newTicketSummary || emailCtx.subject;
      const description = [
        `From: ${emailCtx.from}`,
        `To: ${emailCtx.to.join(', ')}`,
        emailCtx.cc.length ? `CC: ${emailCtx.cc.join(', ')}` : '',
        '',
        notes ? `Notes: ${notes}` : '',
        '',
        '--- Email Body ---',
        emailCtx.body || '(Email body not available)',
      ].filter(Boolean).join('\n');

      const result = await createTicket({
        summary,
        contactId: selectedContact.id,
        companyId: selectedContact.company?.id || 0,
        description,
        boardId: newTicketBoard,
      });

      const newTicket: CWTicket = { id: result.id, summary, closedFlag: false };
      setTickets(prev => [newTicket, ...prev]);
      setSelectedTicket(newTicket);
      setLinkType('ticket');
      setShowCreateTicket(false);
      setNewTicketSummary('');
    } catch (err) {
      setError(`Failed to create ticket: ${String(err).replace(/^Error:\s*/, '')}`);
    }
    setCreatingTicket(false);
  };

  const handleCreateOpp = async () => {
    if (!selectedContact || !emailCtx) return;
    setCreatingOpp(true);
    try {
      const name = newOppName || emailCtx.subject;
      const result = await createOpportunity({
        name,
        contactId: selectedContact.id,
        companyId: selectedContact.company?.id || 0,
      });
      const newOpp: CWOpportunity = { id: result.id, name, closedDate: null };
      setOpportunities(prev => [newOpp, ...prev]);
      setSelectedOpp(newOpp);
      setLinkType('opportunity');
      setShowCreateOpp(false);
      setNewOppName('');
    } catch (err) {
      setError(`Failed to create opportunity: ${String(err).replace(/^Error:\s*/, '')}`);
    }
    setCreatingOpp(false);
  };

  // Load board statuses when a ticket is selected
  useEffect(() => {
    if (selectedTicket?.board?.id) {
      fetchBoardStatuses(selectedTicket.board.id).then(statuses => {
        setBoardStatuses(statuses);
        setNewTicketStatus(selectedTicket.status?.id || null);
      }).catch(() => {});
    } else {
      setBoardStatuses([]);
      setNewTicketStatus(null);
    }
  }, [selectedTicket]);

  useEffect(() => {
    if (addTimeEntry && workTypes.length === 0) {
      fetchWorkTypes().then(wt => {
        setWorkTypes(wt);
        if (wt.length > 0 && !selectedWorkType) {
          setSelectedWorkType(wt[0].id);
          setBillableOption((wt[0].billTime as any) || 'Billable');
        }
      }).catch(() => {});
    }
  }, [addTimeEntry]);

  useEffect(() => {
    if (showCreateTicket && boards.length === 0) {
      fetchServiceBoards().then(b => {
        setBoards(b);
        if (b.length > 0) setNewTicketBoard(b[0].id);
      }).catch(() => {});
    }
  }, [showCreateTicket]);

  // --- Settings view ---
  const { mode: themeMode, setMode: setThemeMode } = useTheme();

  if (showSettings) {
    return (
      <div style={{ padding: '1.25rem' }}>
        <Title3>Settings</Title3>
        <div style={{ marginTop: '1.25rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <Text>Contacts cached: {contactCount}</Text>
          <Text>Last sync: {getLastSyncDate() ? new Date(getLastSyncDate()!).toLocaleString() : 'Never'}</Text>
          <Button icon={<ArrowSync24Regular />} onClick={handleSync} disabled={syncing}>
            {syncing ? 'Syncing...' : 'Sync Contacts'}
          </Button>
          <Divider />
          <Switch
            label="Log time entries on opportunities (pre-sales work)"
            checked={logTimeOnOppsState}
            onChange={(_, d) => {
              setLogTimeOnOppsState(d.checked);
              setLogTimeOnOpps(d.checked);
            }}
          />
          <Switch
            label="Default 'Add time entry' to on when a new email opens"
            checked={defaultAddTimeEntryState}
            onChange={(_, d) => {
              setDefaultAddTimeEntryState(d.checked);
              setDefaultAddTimeEntry(d.checked);
            }}
          />
          <Divider />
          <ThemeToggle mode={themeMode} onChange={setThemeMode} />
          <Divider />
          <Button appearance="subtle" onClick={() => { clearConfig(); onReset(); }}>Reset Configuration</Button>
          <Button onClick={() => setShowSettings(false)}>Back</Button>
        </div>
      </div>
    );
  }

  // --- Main view ---
  return (
    <div style={{ padding: '1.25rem', display: 'flex', flexDirection: 'column', gap: '1.25rem', fontSize: '0.938rem', minHeight: 0 }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Title3>Log to CW</Title3>
        <div style={{ display: 'flex', gap: 4 }}>
          <Tooltip content="Sync contacts" relationship="label">
            <Button icon={<ArrowSync24Regular />} appearance="subtle" size="small" onClick={handleSync} disabled={syncing} />
          </Tooltip>
          <Tooltip content="Settings" relationship="label">
            <Button icon={<Settings24Regular />} appearance="subtle" size="small" onClick={() => setShowSettings(true)} />
          </Tooltip>
        </div>
      </div>

      {/* Email context */}
      {emailCtx && (
        <Card size="small">
          <CardHeader
            image={<Mail24Regular />}
            header={<Text weight="semibold" truncate>{emailCtx.subject}</Text>}
            description={<Text size={200}>From: {emailCtx.from}</Text>}
          />
        </Card>
      )}

      {/* Contact matches */}
      {matches.length === 0 && !syncing && (
        <Text style={{ color: '#999' }}>
          No CW contacts matched.{contactCount === 0 && ' Sync contacts first.'}
        </Text>
      )}

      {matches.map(contact => (
        <Card
          key={contact.id}
          size="small"
          style={{
            cursor: 'pointer',
            border: selectedContact?.id === contact.id ? '2px solid #0078d4' : undefined,
          }}
          onClick={() => {
            setSelectedContact(contact);
            setSelectedCompany(null);
            setSelectedTicket(null);
            setSelectedOpp(null);
            setLinkType('none');
          }}
        >
          <CardHeader
            image={<Person24Regular />}
            header={<Text weight="semibold">{contact.firstName} {contact.lastName}</Text>}
            description={
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <Building24Regular style={{ fontSize: 12 }} />
                <Text size={200}>{contact.company?.name || '(no company)'}</Text>
              </div>
            }
            action={selectedContact?.id === contact.id ? <Badge appearance="filled" color="brand">Selected</Badge> : undefined}
          />
        </Card>
      ))}

      {/* Company search — always available as alternative to contact selection */}
      {!selectedCompany && contactCount > 0 && (
        <div>
          <Text size={200} style={{ color: '#8a8a9a', marginBottom: '0.25rem', display: 'block' }}>
            {matches.length > 0 ? 'Or search by company:' : 'Search by company:'}
          </Text>
          <Input
            placeholder="Type company name..."
            value={companySearch}
            onChange={(_, d) => handleCompanySearch(d.value)}
            style={{ fontSize: '0.938rem' }}
          />
          {searchingCompanies && <Text size={200} style={{ color: '#999', marginTop: '0.25rem' }}>Searching...</Text>}
          {companyResults.map(c => (
            <div
              key={c.id}
              onClick={() => {
                setSelectedCompany(c);
                setSelectedContact(null);
                setCompanySearch('');
                setCompanyResults([]);
                setSelectedTicket(null);
                setSelectedOpp(null);
                setLinkType('none');
              }}
              style={{
                padding: '0.625rem',
                marginTop: '0.375rem',
                borderRadius: '0.375rem',
                border: '1px solid #e0e0e0',
                cursor: 'pointer',
              }}
            >
              <Text size={300} weight="semibold">{c.name}</Text>
              {c.identifier && <Text size={200} style={{ color: '#999', marginLeft: '0.5rem' }}>{c.identifier}</Text>}
            </div>
          ))}
        </div>
      )}
      {selectedCompany && !selectedContact && (
        <div style={{
          padding: '0.625rem',
          borderRadius: '0.375rem',
          border: '2px solid #0078d4',
          background: 'rgba(0,120,212,0.06)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}>
          <div>
            <Text weight="semibold">{selectedCompany.name}</Text>
            <Text size={200} style={{ color: '#999', display: 'block' }}>Company (no contact)</Text>
          </div>
          <Button size="small" appearance="subtle" onClick={() => { setSelectedCompany(null); setTickets([]); setOpportunities([]); }}>Change</Button>
        </div>
      )}

      {/* --- Post-contact-selection section --- */}
      {(selectedContact || selectedCompany) && !logged && (
        <>
          {/* Link to ticket/opp */}
          <Divider style={{ margin: '0.5rem 0' }} />

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.25rem' }}>
            <Text weight="semibold" size={400}>Link to</Text>
            <Switch
              label="Closed"
              checked={includeClosed}
              onChange={(_, d) => setIncludeClosed(d.checked)}
            />
          </div>

          {loadingLinks ? (
            <Spinner size="tiny" label="Loading..." />
          ) : (
            <>
              <TabList
                selectedValue={linkType}
                onTabSelect={(_, d) => { setLinkType(d.value as LinkType); setSelectedTicket(null); setSelectedOpp(null); }}
                size="small"
              >
                <Tab value="none">Contact only</Tab>
                <Tab value="ticket">Tickets ({tickets.length})</Tab>
                <Tab value="opportunity">Opps ({opportunities.length})</Tab>
              </TabList>

              {/* Ticket list */}
              {linkType === 'ticket' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>
                  {tickets.map(t => (
                    <Tooltip
                      key={t.id}
                      relationship="description"
                      content={
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', maxWidth: '22rem' }}>
                          <div style={{ fontWeight: 600 }}>#{t.id} · {t.summary || '—'}</div>
                          {t.board?.name && <div><span style={{ color: '#999' }}>Board:</span> {t.board.name}</div>}
                          {t.status?.name && <div><span style={{ color: '#999' }}>Status:</span> {t.status.name}</div>}
                          {t.priority?.name && <div><span style={{ color: '#999' }}>Priority:</span> {t.priority.name}</div>}
                          {t.company?.name && <div><span style={{ color: '#999' }}>Company:</span> {t.company.name}</div>}
                        </div>
                      }
                    >
                      <div
                        style={{
                          cursor: 'pointer',
                          padding: '0.875rem 0.75rem',
                          borderRadius: '0.375rem',
                          minHeight: '2.5rem',
                          border: selectedTicket?.id === t.id ? '2px solid #0078d4' : '1px solid #e0e0e0',
                          background: selectedTicket?.id === t.id ? 'rgba(0,120,212,0.06)' : 'transparent',
                        }}
                        onClick={() => { setSelectedTicket(t); setSelectedOpp(null); }}
                      >
                        <Text size={300} style={{ lineHeight: 1.4, wordBreak: 'break-word' }}>
                          <span style={{ fontFamily: 'monospace', color: '#666', fontSize: '0.8rem' }}>#{t.id}</span>{' '}
                          {t.summary || '—'}
                        </Text>
                      </div>
                    </Tooltip>
                  ))}
                  {tickets.length === 0 && (
                    <Text size={200} style={{ color: '#999', padding: '4px 0' }}>
                      No {includeClosed ? '' : 'open '}tickets
                    </Text>
                  )}

                  {/* Ticket status update */}
                  {selectedTicket && boardStatuses.length > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.25rem' }}>
                      <Text size={200} style={{ color: '#8a8a9a', whiteSpace: 'nowrap' }}>Status:</Text>
                      <Select
                        value={String(newTicketStatus || '')}
                        onChange={(_, d) => setNewTicketStatus(Number(d.value))}
                        style={{ fontSize: '0.938rem', flex: 1 }}
                      >
                        {boardStatuses.map(s => (
                          <option key={s.id} value={s.id}>{s.name || `Status ${s.id}`}</option>
                        ))}
                      </Select>
                    </div>
                  )}

                  {/* Create new ticket */}
                  {!showCreateTicket ? (
                    <Button
                      icon={<Add24Regular />}
                      size="small"
                      appearance="subtle"
                      style={{ marginTop: 4 }}
                      onClick={() => { setShowCreateTicket(true); setNewTicketSummary(emailCtx?.subject || ''); }}
                    >
                      Create ticket from email
                    </Button>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', padding: '0.75rem', border: '1px solid #e0e0e0', borderRadius: '0.375rem', marginTop: '0.25rem' }}>
                      <Text size={200} weight="semibold">New Ticket</Text>
                      <Input placeholder="Summary" value={newTicketSummary} onChange={(_, d) => setNewTicketSummary(d.value)} style={{ fontSize: '0.938rem' }} />
                      {boards.length > 0 && (
                        <Select value={String(newTicketBoard)} onChange={(_, d) => setNewTicketBoard(Number(d.value))} style={{ fontSize: '0.938rem' }}>
                          {boards.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                        </Select>
                      )}
                      <div style={{ display: 'flex', gap: 6 }}>
                        <Button size="small" appearance="primary" onClick={handleCreateTicket} disabled={creatingTicket || !newTicketSummary}>
                          {creatingTicket ? 'Creating...' : 'Create'}
                        </Button>
                        <Button size="small" onClick={() => setShowCreateTicket(false)}>Cancel</Button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Opportunity list */}
              {linkType === 'opportunity' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>
                  {opportunities.map(o => (
                    <Tooltip
                      key={o.id}
                      relationship="description"
                      content={
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', maxWidth: '22rem' }}>
                          <div style={{ fontWeight: 600 }}>{o.name || '—'}</div>
                          {o.stage?.name && <div><span style={{ color: '#999' }}>Stage:</span> {o.stage.name}</div>}
                          {o.status?.name && <div><span style={{ color: '#999' }}>Status:</span> {o.status.name}</div>}
                          {o.contact?.name && <div><span style={{ color: '#999' }}>Contact:</span> {o.contact.name}</div>}
                          {o.company?.name && <div><span style={{ color: '#999' }}>Company:</span> {o.company.name}</div>}
                        </div>
                      }
                    >
                      <div
                        style={{
                          cursor: 'pointer',
                          padding: '0.875rem 0.75rem',
                          borderRadius: '0.375rem',
                          minHeight: '2.5rem',
                          border: selectedOpp?.id === o.id ? '2px solid #0078d4' : '1px solid #e0e0e0',
                          background: selectedOpp?.id === o.id ? 'rgba(0,120,212,0.06)' : 'transparent',
                        }}
                        onClick={() => { setSelectedOpp(o); setSelectedTicket(null); }}
                      >
                        <Text size={300} style={{ lineHeight: 1.4, wordBreak: 'break-word' }}>{o.name || '—'}</Text>
                        {o.stage?.name && (
                          <Badge appearance="outline" size="small" color="success" style={{ marginTop: '0.25rem' }}>{o.stage.name}</Badge>
                        )}
                      </div>
                    </Tooltip>
                  ))}
                  {opportunities.length === 0 && (
                    <Text size={200} style={{ color: '#999', padding: '4px 0' }}>
                      No {includeClosed ? '' : 'open '}opportunities
                    </Text>
                  )}

                  {/* Create new opportunity */}
                  {!showCreateOpp ? (
                    <Button
                      icon={<Add24Regular />}
                      size="small"
                      appearance="subtle"
                      style={{ marginTop: '0.25rem' }}
                      onClick={() => { setShowCreateOpp(true); setNewOppName(emailCtx?.subject || ''); }}
                    >
                      Create opportunity from email
                    </Button>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', padding: '0.75rem', border: '1px solid #e0e0e0', borderRadius: '0.375rem', marginTop: '0.25rem' }}>
                      <Text size={200} weight="semibold">New Opportunity</Text>
                      <Input placeholder="Opportunity name" value={newOppName} onChange={(_, d) => setNewOppName(d.value)} style={{ fontSize: '0.938rem' }} />
                      <div style={{ display: 'flex', gap: 6 }}>
                        <Button size="small" appearance="primary" onClick={handleCreateOpp} disabled={creatingOpp || !newOppName}>
                          {creatingOpp ? 'Creating...' : 'Create'}
                        </Button>
                        <Button size="small" onClick={() => setShowCreateOpp(false)}>Cancel</Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {/* Notes */}
          <Divider style={{ margin: '0.375rem 0' }} />

          <Text weight="semibold" size={300}>Notes</Text>
          <Textarea
            value={notes}
            onChange={(_, d) => setNotes(d.value)}
            placeholder="Add notes (optional)..."
            resize="vertical"
            style={{ minHeight: '4.5rem', fontSize: '0.938rem' }}
          />

          {/* Time Entry — when a ticket OR (opp + setting on) is selected */}
          {(selectedTicket || (selectedOpp && getLogTimeOnOpps())) && (
            <>
              <Divider style={{ margin: '0.375rem 0' }} />
              <Switch
                label={selectedTicket ? 'Add time entry' : 'Add time entry (pre-sales)'}
                checked={addTimeEntry}
                onChange={(_, d) => setAddTimeEntry(d.checked)}
              />
              {addTimeEntry && (
                <TimeEntryFields
                  workTypes={workTypes}
                  selectedWorkType={selectedWorkType}
                  setSelectedWorkType={setSelectedWorkType}
                  timeHours={timeHours}
                  setTimeHours={setTimeHours}
                  billableOption={billableOption}
                  setBillableOption={setBillableOption}
                  timeNotes={timeNotes}
                  setTimeNotes={setTimeNotes}
                />
              )}
            </>
          )}

          {/* Follow-up */}
          <Divider style={{ margin: '0.375rem 0' }} />
          <Switch
            label="Create follow-up activity"
            checked={createFollowUp}
            onChange={(_, d) => setCreateFollowUp(d.checked)}
          />
          {createFollowUp && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', paddingLeft: '0.25rem' }}>
              <Input
                type="datetime-local"
                value={followUpDate}
                onChange={(_, d) => setFollowUpDate(d.value)}
                style={{ fontSize: '0.938rem' }}
              />
              <Textarea
                placeholder="Follow-up notes..."
                value={followUpNotes}
                onChange={(_, d) => setFollowUpNotes(d.value)}
                resize="vertical"
                style={{ minHeight: '3rem', fontSize: '0.938rem' }}
              />
            </div>
          )}

          {/* Log button */}
          <Divider style={{ margin: '0.375rem 0' }} />
          <Button
            appearance="primary"
            onClick={handleLog}
            disabled={logging}
            icon={logging ? <Spinner size="tiny" /> : undefined}
            style={{ width: '100%' }}
          >
            {logging ? 'Logging...' : `Log Email${selectedTicket ? ` → #${selectedTicket.id}` : selectedOpp ? ` → ${selectedOpp.name}` : ''} — ${selectedContact ? `${selectedContact.firstName} ${selectedContact.lastName}` : selectedCompany?.name || ''}`}
          </Button>
        </>
      )}

      {/* Success */}
      {logged && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: 14, background: 'rgba(0,120,0,0.08)', borderRadius: 8 }}>
            <CheckmarkCircle24Filled color="green" />
            <div>
              <Text weight="semibold">Logged to ConnectWise!</Text>
              {loggedId && <Text size={200} style={{ display: 'block' }}>Activity #{loggedId}</Text>}
              {selectedTicket && <Text size={200} style={{ display: 'block' }}>Linked to ticket #{selectedTicket.id}</Text>}
              {selectedOpp && <Text size={200} style={{ display: 'block' }}>Linked to {selectedOpp.name}</Text>}
              {followUpCreated && <Text size={200} style={{ display: 'block' }}>Follow-up created</Text>}
              {timeEntryCreated && <Text size={200} style={{ display: 'block' }}>Time entry logged ({timeHours}h)</Text>}
            </div>
          </div>
          <Button appearance="subtle" onClick={loadEmail} style={{ width: '100%' }}>
            Log another email
          </Button>
        </div>
      )}

      {/* Error */}
      {error && <Text style={{ color: 'red', fontSize: 12 }}>{error}</Text>}

      {/* Footer */}
      <div style={{ marginTop: 'auto', paddingTop: 8 }}>
        <Text size={200} style={{ color: '#666' }}>
          {contactCount} contacts{syncing && ` — ${syncProgress || 'syncing...'}`}
        </Text>
      </div>
    </div>
  );
}
